/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// MUI v4 Button — NS/Tractie component overrides.
// Family-anatomy: plugins/ns-theme/docs/family-anatomy/button.md
// Ground rules: plugins/ns-theme/docs/ground-rules.md "Focus" and "Subtractive first".
//
// Tractie deviations from MUI defaults:
//  - textTransform: none (geen ALLCAPS)
//  - borderRadius: 6 (radius-m)
//  - boxShadow: none over alle states (Tractie kent geen elevation op button)
//  - transition: none (Tractie state-changes zijn instant)
//  - geen size-variants (one-size — alle MUI sizes plat naar M = 40px)
//  - geen *Secondary color-prop (MUI's pink/magenta neutraliseren)
//
// MUI v4 Button defaults (bron: @material-ui/core/Button/Button.js) — reset-checklist (ground-rules.md "Subtractive first"):
//   root.fontFamily             → '"Roboto","Helvetica","Arial",sans-serif'   reset → var(--ns-font-family-regular)
//   root.color                  → 'rgba(0, 0, 0, 0.87)'                       reset → var(--ns-color-fg-default)
//   root.transition             → MUI shorthand (background, box-shadow…)     reset → 'none'
//   root.&$disabled.color       → 'rgba(0, 0, 0, 0.26)'                       reset → var(--ns-color-fg-disabled)
//   contained.boxShadow         → MUI elevation 2/4/8                         reset → 'none' (alle states)
//   contained.bg                → theme.palette.grey[300]                     reset → var(--ns-color-bg-interactive-default)
//   containedPrimary.bg         → theme.palette.primary.main (#1976d2)        reset → var(--ns-color-bg-interactive-default)
//   containedSecondary.bg       → theme.palette.secondary.main (#f50057)      reset → contained-alias (geen NS-secondary)
//   outlined.border             → '1px solid rgba(0, 0, 0, 0.23)'             reset → 1px solid var(--ns-color-border-interactive-default)
//   outlined.&$disabled.border  → '1px solid rgba(0, 0, 0, 0.12)'             reset → 1px solid var(--ns-color-border-default)
//   outlinedPrimary.border      → '1px solid rgba(25, 118, 210, 0.5)' (#1976d2) reset → var(--ns-color-border-interactive-default)
//   outlinedPrimary.color       → '#1976d2'                                   reset → var(--ns-color-fg-interactive-default)
//   outlinedSecondary.color     → '#f50057'                                   reset → outlined-alias (geen NS-secondary)
//   textPrimary.color           → '#1976d2'                                   reset → var(--ns-color-fg-interactive-default)
//   textSecondary.color         → '#f50057'                                   reset → text-alias (geen NS-secondary)
//   sizeSmall/sizeLarge slots   → fontSize 0.875/0.9375rem + padding shifts   reset → {} (one-size, density-decision)
//   focusVisible (default MUI)  → inner box-shadow ring                       reset → Tractie focus-formule (outline+offset+shadow op root)
//
// Focus-formule (Tractie spec, geverifieerd 2026-05-21 tegen tractie-button live
// rendering + button.css.js `--_outline-offset: var(--tractie-border-width-thin)`):
//   outline:        var(--ns-border-width-medium) solid var(--ns-color-border-focus);
//   outline-offset: var(--ns-border-width-thin);   // OUTSET +1px buiten button
//   box-shadow:     var(--ns-shadow-elevated-1);
// Toegepast op root (`&$focusVisible`), niet op label/icon-slots.
// (Eerdere INSET-variant calc(-1 * border-width-medium) week af van Tractie en
// BUI B4b primary `outline-offset: 1px`. Bart-feedback 2026-05-21 T1.4.)

const focusVisibleOnRoot = {
  outline: 'var(--ns-border-width-medium) solid var(--ns-color-border-focus)',
  outlineOffset: 'var(--ns-border-width-thin)',
  boxShadow: 'var(--ns-shadow-elevated-1)',
} as const;

export const MuiButtonLightOverrides = {
  // Root: focus-anchor + base-typografie + base-padding (override-volgorde 1).
  root: {
    fontFamily: 'var(--ns-font-family-regular)',
    fontSize: 'var(--ns-font-size-m)',
    fontWeight: 700,
    letterSpacing: 'normal',
    boxSizing: 'border-box' as const,
    lineHeight: 1,
    minWidth: 40,
    minHeight: 40,
    textTransform: 'none' as const,
    borderRadius: 6,
    color: 'var(--ns-color-fg-default)',
    padding: '8px 16px',
    transition: 'none',
    '&:hover, &.force-hover': {
      backgroundColor: 'var(--ns-color-bg-hover)',
      textDecoration: 'none',
      '&$disabled': {
        backgroundColor: 'transparent',
      },
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&$focusVisible, &.force-focus-visible': focusVisibleOnRoot,
    '&$disabled': {
      color: 'var(--ns-color-fg-disabled)',
    },
    // Cross-cluster fix — Tractie no-level ghost-variant (Clusters 7 + 14).
    // Tractie rendert `<TractieButton>` zonder `level` als ghost: transparent bg +
    // fg-default (dark grey). MUI/BUI library-defaults hebben geen ghost-variant;
    // story zet `data-no-level=""` attribuut wanneer cluster.level === undefined.
    // Spirit-volging: forceer richting Tractie ghost-spec via override-laag.
    // Hoge specificity (root + attribute) wint van variant-slot bg-rules.
    '&[data-no-level]': {
      backgroundColor: 'transparent',
      color: 'var(--ns-color-fg-default)',
      border: 'none',
      boxShadow: 'none',
      '&:hover, &.force-hover': {
        backgroundColor: 'var(--ns-color-bg-hover)',
        boxShadow: 'none',
      },
      '&:active, &.force-active': {
        backgroundColor: 'var(--ns-color-bg-pressed)',
        boxShadow: 'none',
      },
      '&$disabled': {
        backgroundColor: 'transparent',
        color: 'var(--ns-color-fg-default)',
        opacity: 0.25,
      },
    },
    // Cross-cluster fix — Tractie icon-only square (Clusters 1, 8 — propageert naar
    // alle clusters met state=icon-only). Tractie iconOnly rendert 40×40 (m) of
    // 32×32 (s) vierkant; MUI behoudt default horizontale padding (56×40 / 54×38).
    // Story zet `data-icon-only=""` attribuut wanneer state === 'icon-only'.
    // Spirit-volging: width gelijk aan min-height (square) + padding-inline 0.
    // min-height erft uit sizeSmall (32) of root (40); width volgt via min-width.
    '&[data-icon-only]': {
      minWidth: 40,
      width: 40,
      height: 40,
      padding: 0,
      '&$sizeSmall': {
        minWidth: 32,
        width: 32,
        height: 32,
        padding: 0,
      },
    },
  },

  // Label: layout-only — geen kleur (erft via root), geen padding (zit op root).
  label: {
    alignItems: 'inherit' as const,
    display: 'inherit' as const,
    justifyContent: 'inherit' as const,
    width: '100%',
  },

  // Variant: contained — primary CTA (Tractie variant primary).
  // Tractie disabled-strategie: behoud default bg + fg, dim met opacity 0.25
  // (NIET MUI's grijs-shift). Zie ground-rules.md "Disabled state".
  contained: {
    backgroundColor: 'var(--ns-color-bg-interactive-default)',
    color: 'var(--ns-color-fg-solid)',
    boxShadow: 'none',
    '&:hover, &.force-hover': {
      backgroundColor: 'var(--ns-color-bg-interactive-hover)',
      boxShadow: 'none',
      '@media (hover: none)': {
        boxShadow: 'none',
        backgroundColor: 'var(--ns-color-bg-interactive-default)',
      },
      '&$disabled': {
        backgroundColor: 'var(--ns-color-bg-interactive-default)',
      },
    },
    '&:active, &.force-active': {
      backgroundColor: 'var(--ns-color-bg-interactive-pressed)',
      boxShadow: 'none',
    },
    '&$focusVisible, &.force-focus-visible': {
      ...focusVisibleOnRoot,
    },
    '&$disabled': {
      boxShadow: 'none',
      color: 'var(--ns-color-fg-solid)',
      backgroundColor: 'var(--ns-color-bg-interactive-default)',
      opacity: 0.25,
    },
  },

  // containedPrimary: alias voor contained (Tractie kent geen secondary-color-prop).
  containedPrimary: {
    backgroundColor: 'var(--ns-color-bg-interactive-default)',
    color: 'var(--ns-color-fg-solid)',
    '&:hover, &.force-hover': {
      backgroundColor: 'var(--ns-color-bg-interactive-hover)',
      '@media (hover: none)': {
        backgroundColor: 'var(--ns-color-bg-interactive-default)',
      },
    },
    '&$disabled': {
      color: 'var(--ns-color-fg-solid)',
      backgroundColor: 'var(--ns-color-bg-interactive-default)',
      opacity: 0.25,
    },
  },

  // containedSecondary: DESTRUCTIVE-mapping (Tractie appearance='destructive').
  // reason: Tractie kent geen secondary-color-prop, maar de comparison-story routeert
  // appearance='destructive' → MUI color='secondary'. We benutten dit slot om de
  // Tractie destructive-variant te leveren (rood, niet MUI's pink #f50057).
  // Disabled-strategie: Tractie behoudt default destructive-bg + opacity 0.25.
  containedSecondary: {
    backgroundColor: 'var(--ns-color-bg-destructive-default)',
    color: 'var(--ns-color-fg-solid)',
    '&:hover, &.force-hover': {
      backgroundColor: 'var(--ns-color-bg-destructive-hover)',
      '@media (hover: none)': {
        backgroundColor: 'var(--ns-color-bg-destructive-default)',
      },
    },
    '&:active, &.force-active': {
      backgroundColor: 'var(--ns-color-bg-destructive-pressed)',
    },
    '&$disabled': {
      color: 'var(--ns-color-fg-solid)',
      backgroundColor: 'var(--ns-color-bg-destructive-default)',
      opacity: 0.25,
    },
  },

  // Variant: outlined — secondary CTA (Tractie variant secondary).
  // Padding 7px 15px = 8px 16px - 1px border-compensatie zodat root-height stabiel = 40px.
  // Tractie disabled-strategie: behoud interactive border + fg, dim met opacity 0.25
  // (NIET MUI's grey-border/grey-fg shift). Spiegel-pattern van contained.&$disabled.
  outlined: {
    border: '1px solid var(--ns-color-border-interactive-default)',
    color: 'var(--ns-color-fg-interactive-default)',
    padding: '7px 15px',
    '&:hover, &.force-hover': {
      border: '1px solid var(--ns-color-border-interactive-hover)',
      backgroundColor: 'var(--ns-color-bg-hover)',
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&:active, &.force-active': {
      border: '1px solid var(--ns-color-border-interactive-pressed)',
      backgroundColor: 'var(--ns-color-bg-pressed)',
    },
    '&$disabled': {
      border: '1px solid var(--ns-color-border-interactive-default)',
      color: 'var(--ns-color-fg-interactive-default)',
      opacity: 0.25,
    },
  },

  // outlinedPrimary: alias voor outlined (Tractie variant secondary, level=secondary).
  // Disabled-strategie: behoud interactive tokens + opacity 0.25 (zie outlined).
  outlinedPrimary: {
    border: '1px solid var(--ns-color-border-interactive-default)',
    color: 'var(--ns-color-fg-interactive-default)',
    '&:hover, &.force-hover': {
      border: '1px solid var(--ns-color-border-interactive-hover)',
      backgroundColor: 'var(--ns-color-bg-hover)',
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&$disabled': {
      border: '1px solid var(--ns-color-border-interactive-default)',
      color: 'var(--ns-color-fg-interactive-default)',
      opacity: 0.25,
    },
  },

  // outlinedSecondary: DESTRUCTIVE-mapping voor outlined-variant (Tractie level=secondary + appearance=destructive).
  // reason: Tractie kent geen secondary-color-prop op outlined; de comparison-story
  // routeert appearance='destructive' → MUI color='secondary' → outlinedSecondary slot.
  // We benutten dit slot om de Tractie destructive-outlined-variant te leveren
  // (transparante bg + destructive-rode border + destructive-rode fg).
  // Disabled-strategie: behoud destructive tokens + opacity 0.25 (spiegel van outlined.&$disabled).
  outlinedSecondary: {
    border: '1px solid var(--ns-color-border-destructive-default)',
    color: 'var(--ns-color-fg-destructive-default)',
    '&:hover, &.force-hover': {
      border: '1px solid var(--ns-color-border-destructive-default)',
      backgroundColor: 'var(--ns-color-bg-hover)',
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&:active, &.force-active': {
      border: '1px solid var(--ns-color-border-destructive-default)',
      backgroundColor: 'var(--ns-color-bg-pressed)',
    },
    '&$disabled': {
      border: '1px solid var(--ns-color-border-destructive-default)',
      color: 'var(--ns-color-fg-destructive-default)',
      opacity: 0.25,
    },
  },

  // Variant: text — tertiary action (Tractie variant tertiary).
  text: {
    color: 'var(--ns-color-fg-interactive-default)',
    padding: '8px 16px',
    '&:hover, &.force-hover': {
      backgroundColor: 'var(--ns-color-bg-hover)',
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&:active, &.force-active': {
      backgroundColor: 'var(--ns-color-bg-pressed)',
      color: 'var(--ns-color-fg-interactive-pressed)',
    },
    '&$disabled': {
      color: 'var(--ns-color-fg-disabled)',
    },
  },

  // textPrimary: tertiary action — Tractie level=tertiary, appearance=undefined.
  // Tractie spec (gemeten live, light): bg transparent, fg interactive-blauw,
  // fontWeight 700, geen border, opacity 0.25 op disabled/loading.
  // MUI default `&$disabled` rendert `rgba(0,0,0,0.28)` (zwart-met-alpha); we vervangen
  // dat door interactive-fg + opacity 0.25 conform Tractie disabled-pattern (cf. text/
  // outlined `&$disabled` strategie elders in dit bestand).
  textPrimary: {
    color: 'var(--ns-color-fg-interactive-default)',
    '&:hover, &.force-hover': {
      backgroundColor: 'var(--ns-color-bg-hover)',
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&:active, &.force-active': {
      backgroundColor: 'var(--ns-color-bg-pressed)',
      color: 'var(--ns-color-fg-interactive-pressed)',
    },
    '&$disabled': {
      color: 'var(--ns-color-fg-interactive-default)',
      opacity: 0.25,
    },
    // Cross-cluster fix — Tractie tertiary m icon-only square 32×32 (Clusters 5 + 6).
    // Tractie volgt (besluit Bart 2026-05-20): Tractie rendert tertiary level (text*
    // variant) icon-only m als 32×32, niet 40×40 conform eigen size=m spec. We volgen
    // Tractie zelfs als dit afwijkt van de m-spec elders. Override root.&[data-icon-only]
    // (40×40 voor m) terug naar 32×32 specifiek voor textPrimary (tertiary). Size=s
    // (sizeSmall) blijft 32×32 via root-rule, dus geen extra override nodig.
    '&[data-icon-only]:not($sizeSmall)': {
      minWidth: 32,
      minHeight: 32,
      width: 32,
      height: 32,
      padding: 0,
    },
  },

  // textSecondary: DESTRUCTIVE-mapping voor text-variant (Tractie level=tertiary + appearance=destructive).
  // reason: Tractie kent geen secondary-color-prop op text; de comparison-story
  // routeert appearance='destructive' → MUI color='secondary' → textSecondary slot.
  // We benutten dit slot om de Tractie destructive-tertiary-variant te leveren
  // (transparante bg + destructive-rode fg, fontWeight 700 erfde via root).
  // Disabled-strategie: behoud destructive-fg + opacity 0.25 (spiegel van textPrimary.&$disabled
  // en outlinedSecondary.&$disabled). MUI default rendert disabled met rgba(0,0,0,0.28).
  textSecondary: {
    color: 'var(--ns-color-fg-destructive-default)',
    '&:hover, &.force-hover': {
      backgroundColor: 'var(--ns-color-bg-hover)',
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&:active, &.force-active': {
      backgroundColor: 'var(--ns-color-bg-pressed)',
      color: 'var(--ns-color-fg-destructive-default)',
    },
    '&$disabled': {
      color: 'var(--ns-color-fg-destructive-default)',
      opacity: 0.25,
    },
    // Cross-cluster fix — Tractie tertiary destructive m icon-only square 32×32
    // (Cluster 6). Spiegel van textPrimary-rule. Tractie rendert tertiary +
    // appearance=destructive icon-only m als 32×32. Zie textPrimary comment voor
    // achtergrond (besluit Bart 2026-05-20: Tractie volgen).
    '&[data-icon-only]:not($sizeSmall)': {
      minWidth: 32,
      minHeight: 32,
      width: 32,
      height: 32,
      padding: 0,
    },
  },

  // disableElevation: Tractie button heeft nooit elevation; alle states boxShadow none.
  disableElevation: {
    boxShadow: 'none',
    '&:hover, &.force-hover': {
      boxShadow: 'none',
    },
    '&:active, &.force-active': {
      boxShadow: 'none',
    },
    '&$focusVisible, &.force-focus-visible': {
      boxShadow: 'none',
    },
    '&$disabled': {
      boxShadow: 'none',
    },
  },

  // colorInherit: laat content-color erven (gebruik in Tractie zelden, MUI-default behouden).
  colorInherit: {
    color: 'inherit',
    borderColor: 'currentColor',
  },

  focusVisible: {}, // reason: state-marker only; werkelijke focus-styling staat in root.&$focusVisible + contained.&$focusVisible (per MUI v4 specificity-pattern; standalone slot triggert MUI runtime warning over CSS-specificity)
  disabled: {}, // reason: state-marker; werkelijke disabled-styling staat per variant via &$disabled
  fullWidth: {
    width: '100%',
  },

  // Icon-slots: margin + color: inherit (matched aan label).
  // Icon-size altijd 20px (M-only) — Tractie one-size.
  startIcon: {
    display: 'inherit' as const,
    marginLeft: -4,
    marginRight: 8,
    color: 'inherit',
    '&$iconSizeSmall': {
      marginLeft: -2,
    },
  },

  endIcon: {
    display: 'inherit' as const,
    marginLeft: 8,
    marginRight: -4,
    color: 'inherit',
    '&$iconSizeSmall': {
      marginRight: -2,
    },
  },

  // Size-variants — Tractie two-size besluit (M = 40px default + S = 32px).
  // reason: Cluster 8 (T1.1 atoms-batch) — Tractie size=s heeft eigen spec
  // (32px min-height, 14px font-size, 7px/15px padding). MUI size='small' mapt
  // hierop. Large blijft geneutraliseerd (Tractie kent geen L). Zie
  // ground-rules.md "Density" en bz0s-B-atoms/button/primary-s.
  //
  // Tractie size=s spec (gemeten live op tractie-button[size="s"][level="primary"]):
  //   min-height: 32px, font-size: 14px (var(--ns-font-size-s)),
  //   line-height: 1, padding: 7px 15px (contained), 6px 14px (outlined -1px border),
  //   icon-size: 16px, padding (icon-only): 3px (square).
  containedSizeSmall: {
    fontSize: 'var(--ns-font-size-s)',
    lineHeight: 1,
    minHeight: 32,
    minWidth: 32,
    padding: '7px 15px',
  },
  containedSizeLarge: {}, // reason: Tractie kent geen size=l; large flat naar medium
  outlinedSizeSmall: {
    fontSize: 'var(--ns-font-size-s)',
    lineHeight: 1,
    minHeight: 32,
    minWidth: 32,
    padding: '6px 14px',
  },
  outlinedSizeLarge: {}, // reason: Tractie kent geen size=l; large flat naar medium
  textSizeSmall: {
    fontSize: 'var(--ns-font-size-s)',
    lineHeight: 1,
    minHeight: 32,
    minWidth: 32,
    padding: '7px 15px',
  },
  textSizeLarge: {}, // reason: Tractie kent geen size=l; large flat naar medium
  sizeSmall: {
    fontSize: 'var(--ns-font-size-s)',
    lineHeight: 1,
    minHeight: 32,
    minWidth: 32,
  },
  sizeLarge: {}, // reason: Tractie kent geen size=l; large flat naar medium

  // Icon-size slots: 20px (M) only — Tractie one-size, S/L flat naar M.
  iconSizeMedium: {
    '& > *:first-child': {
      fontSize: 20,
    },
  },
  iconSizeSmall: {
    '& > *:first-child': {
      fontSize: 20,
    },
  }, // reason: Tractie one-size besluit; icon-S flat naar M (20px)
  iconSizeLarge: {
    '& > *:first-child': {
      fontSize: 20,
    },
  }, // reason: Tractie one-size besluit; icon-L flat naar M (20px)
} as const;

// Dark palette: identiek aan Light — alle waarden zijn NS-tokens met cluster-flip
// (zie tokens.css [data-theme-mode='dark']). Geen mode-specifieke literals meer nodig.
export const MuiButtonDarkOverrides = MuiButtonLightOverrides;
